import { useState, useCallback } from 'react';
import { useAppDispatch } from '../app/hooks';
import { gqlClient } from '../components/AppProvider';
import { setToastConfig } from '../features/EDIContainer/EDIContainerSlice';
import { ToastSeverity } from '../util/Constants';
import { getErrorMessage } from '../util/Common';
import { MutationUploadBlogStorage } from '../gql/MutationExecuteBlobStorageUpload';
import { 
    BlobUploadRequestInput,
    ActionPayload,
    RequestResult 
} from '../gql-types.generated';

/**
 * Custom hook for uploading blob storage version files
 * Handles the upload mutation and status management
 * 
 * @returns Object containing uploadVersion function, uploadStatus, and isUploading state
 */
export const useBlobStorageVersionUpload = () => {
    const dispatch = useAppDispatch();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<ActionPayload | undefined>(undefined);

    /**
     * Uploads a new version of a blob storage file
     * 
     * @param fileUploadData - The upload request data including file content, blob document type, etc.
     */
    const uploadVersion = useCallback(async (fileUploadData: BlobUploadRequestInput) => {
        setIsUploading(true);
        setUploadStatus(undefined);
        
        try {
            const status = await MutationUploadBlogStorage(gqlClient, fileUploadData);
           
            if (status) {
                if (status.result === RequestResult.Fail) {
                    dispatch(setToastConfig({
                        message: status.message as string,
                        severity: ToastSeverity.Error
                    }));
                    getErrorMessage(status.message as string, status.errors, true, true);
                } else if (status.result === RequestResult.Success) {
                    dispatch(setToastConfig({
                        message: status.message as string,
                        severity: ToastSeverity.Success
                    }));
                }
                setUploadStatus(status);
            }
        } catch (e) {
            const errorMessage = (e as Error).message;
            dispatch(setToastConfig({
                message: errorMessage,
                severity: ToastSeverity.Error
            }));
            setUploadStatus({
                result: RequestResult.Fail,
                message: errorMessage
            } as ActionPayload);
        } finally {
            setIsUploading(false);
        }
    }, [dispatch]);

    const clearUploadStatus = useCallback(() => {
        setUploadStatus(undefined);
    }, []);

    return { uploadVersion, uploadStatus, isUploading, clearUploadStatus };
};